import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pb24_customer_token";

const Ctx = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const refresh = useCallback(async (t) => {
    const tk = t ?? token;
    if (!tk) { setCustomer(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/customer/me`, { headers: { Authorization: `Bearer ${tk}` } });
      setCustomer(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/customer/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setCustomer(data.customer);
    return data.customer;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/customer/register`, payload);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setCustomer(data.customer);
    return data.customer;
  };

  // Set an already-issued token (e.g. from quick-signup)
  const applyToken = (newToken, newCustomer) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    if (newCustomer) setCustomer(newCustomer);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setCustomer(null);
  };

  const authFetch = (path, opts = {}) => {
    const { headers: optHeaders, ...rest } = opts;
    return axios({
      url: `${API}${path}`,
      ...rest,
      // Auth-Header ZULETZT setzen, damit er nicht durch opts.headers überschrieben wird.
      headers: {
        ...(optHeaders || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  };

  return (
    <Ctx.Provider value={{ token, customer, loading, login, register, logout, refresh, authFetch, applyToken }}>
      {children}
    </Ctx.Provider>
  );
}

export const useCustomerAuth = () => useContext(Ctx);
